<script>
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import TranscriptHeaderMeta from '@modules/crm/components/transcript-header-meta/transcript-header-meta.vue'
import TranscriptLinkFields from '@modules/crm/components/transcript-link-fields/transcript-link-fields.vue'
import TranscriptSentimentCard from '@modules/crm/components/transcript-sentiment-card/transcript-sentiment-card.vue'
import GeneralAlert from '@modules/global/components/general-alert.vue'

export default {
  name: 'DetailMeetingHeader',
  components: {
    TranscriptHeaderMeta,
    TranscriptLinkFields,
    TranscriptSentimentCard,
    GeneralAlert,
  },
  props: {
    transcript: { type: Object, default: null },
    startTimeFormatted: { type: String, default: '-' },
    duration: { type: String, default: '-' },
    regenerating: { type: Boolean, default: false },
  },
  computed: {
    // Acesso seguro à transcrição: evita repetir `this.transcript && ...` em
    // todo computed. As leituras abaixo derivam direto deste objeto.
    safeTranscript() {
      return this.transcript || {}
    },
    profissionalNome() {
      return this.safeTranscript.profissional_nome || null
    },
    clientNome() {
      return this.safeTranscript.client_nome || null
    },
    projectNome() {
      return this.safeTranscript.project_name || null
    },
    transcriptStatus() {
      return this.safeTranscript.status || ''
    },
    aiStatus() {
      return this.safeTranscript.ai_status || ''
    },
    aiErrorMessage() {
      return this.safeTranscript.ai_error_message || ''
    },
    hasAiError() {
      return this.aiStatus === 'failed' && Boolean(this.aiErrorMessage)
    },
    sentimento() {
      return this.safeTranscript.sentimento_atual || null
    },
    sentimentoJustificativa() {
      return this.safeTranscript.sentimento_justificativa || ''
    },
    sentimentoAlinhamento() {
      return this.safeTranscript.sentimento_alinhamento_objetivos || ''
    },
    // Nota da reunião (0-10) — a mesma exibida na aba Avaliação.
    projectScore() {
      const score = this.safeTranscript.project_score
      return score === undefined ? null : score
    },
  },
  methods: {
    // A edição do cliente vinculado é feita por um modal dedicado
    // (TranscriptClientModal), aberto via GlobalBus. O modal cuida de carregar a
    // lista de clientes, validar e salvar — aqui só disparamos a abertura.
    openClientModal() {
      GlobalBus.$emit(MODAL.TRANSCRIPT_CLIENT, {
        id: this.safeTranscript.id || null,
        id_cliente: this.safeTranscript.id_cliente_app || null,
      })
    },
    // Vínculo de projeto: modal dedicado (TranscriptLinkProjectModal), aberto via
    // GlobalBus — mesmo padrão do cliente. id_project null desvincula.
    openProjectModal() {
      GlobalBus.$emit(MODAL.TRANSCRIPT_PROJECT, {
        id: this.safeTranscript.id || null,
        id_project: this.safeTranscript.id_project || null,
      })
    },
    handleRegenerate() {
      this.$emit('regenerate')
    },
  },
}
</script>

<template>
  <div>
    <TranscriptHeaderMeta
      class="mb-4"
      :start-time-formatted="startTimeFormatted"
      :duration="duration"
      :status="transcriptStatus"
      :ai-status="aiStatus"
      :profissional="profissionalNome"
    />

    <TranscriptLinkFields
      class="mb-4"
      :client-name="clientNome"
      :project-name="projectNome"
      @edit-client="openClientModal"
      @edit-project="openProjectModal"
    />

    <TranscriptSentimentCard
      class="mb-4"
      :sentimento="sentimento"
      :justificativa="sentimentoJustificativa"
      :alinhamento="sentimentoAlinhamento"
      :score="projectScore"
    />

    <!-- Banner de erro de IA (sempre visível no topo) -->
    <div v-if="hasAiError" class="mb-4">
      <GeneralAlert status="error_v3" center>
        <p class="az-text-primary az-size-14 font-semibold mb-1">
          Erro ao gerar resumo
        </p>
        <span class="az-text-small">{{ aiErrorMessage }}</span>
        <template v-slot:button>
          <BaseButton
            sm
            fitContent
            primary
            class="mr-3"
            :loading="regenerating"
            @click="handleRegenerate"
          >
            Tentar novamente
          </BaseButton>
        </template>
      </GeneralAlert>
    </div>

    <!-- Separador entre o cabeçalho (metadados/status) e as abas do modal -->
    <v-divider />
  </div>
</template>
